import React from "react";
import { Card, Col, Row, Table, Badge } from "react-bootstrap";

/**
 * In-dashboard wiki: how the Restaurant vertical operates across Lusuku.
 *
 * IMPORTANT — content policy for this file:
 *   - No credentials, API keys, tokens, connection strings.
 *   - No internal hostnames or ports that aren't already public.
 *   - Development-only guidance must be gated behind NODE_ENV so CRA
 *     dead-code-eliminates it from the production bundle.
 */
const IS_DEV = process.env.NODE_ENV === "development";

const ACCENT = "#17385a";
const TINT = "#17385a14";
const PILLTEXT = "#17385a";
const MUTED = "#6b7a99";
const devNoteStyle = { background: "#fff8ea", border: "1px solid #f1e0a8", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12.5 };

const titleStyle = { fontSize: 13.5, fontWeight: 700, color: "#1a2b4a" };
const cardStyle = { border: "1px solid #e5e9f1", borderRadius: 8, boxShadow: "none" };
const bodyText = { fontSize: 12.5 };
const listStyle = { paddingLeft: 18, fontSize: 12.5 };
const tableStyle = { fontSize: 12 };
const theadStyle = { background: "#f5f7fb" };

const pillStyle = {
  display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700,
  letterSpacing: "0.3px", textTransform: "uppercase", background: TINT, color: PILLTEXT,
  marginRight: 6, marginBottom: 4,
};

const Pill = ({ label }) => <span style={pillStyle}>{label}</span>;

const SectionIcon = ({ cls }) => (
  <i className={cls} style={{ fontSize: 15, color: ACCENT, marginRight: 8, verticalAlign: "middle" }} />
);

const QA = ({ q, a }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a2b4a" }}>{q}</div>
    <div style={{ fontSize: 12.5, color: MUTED, marginTop: 2 }}>{a}</div>
  </div>
);

const LIFECYCLE = [
  ["PLACED", "Customer submits cart → payment charge attempt"],
  ["CONFIRMED", "Restaurant accepts → kitchen starts"],
  ["READY", "Food prepared, awaiting pickup"],
  ["ASSIGNED", "Rider / 3PL partner accepts dispatch"],
  ["PICKED_UP", "Rider has the order"],
  ["DELIVERED", "Customer confirms receipt"],
  ["SETTLED", "Wallet sweeps commission + releases payout"],
];

const FEE_TIERS = [
  ["0 – 3 km", "1,000"],
  ["3 – 8 km", "700"],
  ["8 – 15 km", "550"],
  ["15+ km", "450"],
];

const SURFACES = [
  ["Restaurants (RESTAURANT group)", <code>/admin/restaurants</code>, "Vendor profiles + menu drawer"],
  ["Restaurant order details", <code>/admin/restaurantOrders-mng/:orderId</code>, "Order detail + rider assignment"],
  ["Delivery fee settings", <code>/admin/delivery-fee-settings</code>, "Pricing config + fee simulator (super-admin)"],
  ["In-house order entry", <em>modal on vendor dashboards</em>, "“+ New In-House Order” slide panel"],
];

const stepStyle = { background: "#f8fafc", border: "1px solid #e5e9f1", borderRadius: 8, padding: 10, height: "100%" };

const RestaurantPlatformWiki = () => {
  return (
    <div>
      {/* Compact header */}
      <div style={{ borderLeft: `3px solid ${ACCENT}`, paddingLeft: 12, marginBottom: 18 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: "#1a2b4a" }}>Restaurants on Lusuku</div>
        <div style={{ fontSize: 12.5, color: MUTED, marginTop: 2 }}>
          Staff reference for the Restaurant vertical: onboarding, menus &amp; hours, ordering surfaces, order
          lifecycle, delivery fees, COD rules, and how restaurant money settles.
        </div>
      </div>

      {IS_DEV && (
        <div style={devNoteStyle}>
          <span style={{ fontWeight: 700, color: "#7a5a00" }}>Local development only.</span>{" "}
          <span className="text-muted">
            Known data quirk: <code>RestaurantProduct.restaurantId</code> /{" "}
            <code>RestaurantSection.restaurantId</code> sometimes store the owner&rsquo;s <em>User</em>{" "}
            <code>_id</code>, not the Restaurant&rsquo;s — never <code>.populate()</code> or{" "}
            <code>findById()</code> alone; use the dual-lookup matching <code>_id</code> OR <code>owner</code>{" "}
            (the long-standing ID-mismatch bug).
          </span>
        </div>
      )}

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-satisfied" />
            At a glance — what a restaurant is on Lusuku
          </Card.Title>
          <div className="mb-2">
            {["B2C food", "Expo + Web shopper", "Open hours", "Outlets", "POS", "Rider delivery"].map((l) => (
              <Pill key={l} label={l} />
            ))}
          </div>
          <p className="text-muted mb-0" style={bodyText}>
            A <strong>Restaurant</strong> is one of the four seller types created by public vendor
            registration (RESTAURANT / RETAILER / WHOLESALER / HEALTHCARE). Each vendor has exactly one
            operation mode; outlets inherit the parent&rsquo;s mode. Restaurants are sold on{" "}
            <strong>both</strong> the Expo app and the web storefront, with open hours gating availability.
          </p>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card className="h-100" style={cardStyle}>
            <Card.Body>
              <Card.Title style={titleStyle}>
                <SectionIcon cls="nc-icon nc-badge" />
                1. Onboarding &amp; vendor registration
              </Card.Title>
              <p className="text-muted mb-2" style={bodyText}>
                <code>POST /seller/register-seller</code> creates a <code>Users</code> record with{" "}
                <code>operationMode: &quot;RESTAURANT&quot;</code> and auto-creates the linked Restaurant
                profile.
              </p>
              <ul className="mb-0 text-muted" style={listStyle}>
                <li>
                  <code>sellerVerification</code> starts <code>PENDING</code>; an admin sets{" "}
                  <code>VERIFIED</code> before products can be created.
                </li>
                <li>
                  Products get a second approval — PENDING &rarr; VERIFIED / REJECTED, verifier and date
                  tracked.
                </li>
                <li>Owner, manager and staff roles with scoped permissions.</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100" style={cardStyle}>
            <Card.Body>
              <Card.Title style={titleStyle}>
                <SectionIcon cls="nc-icon nc-paper-2" />
                2. Menu, catalogue &amp; hours
              </Card.Title>
              <p className="text-muted mb-2" style={bodyText}>
                Menus are stored as restaurant sections (Appetizers, Drinks, &hellip;) containing restaurant
                products — each item with price, image, stock (<code>countInStock</code>) and enable/disable.
              </p>
              <ul className="mb-0 text-muted" style={listStyle}>
                <li>
                  <strong>Menu drawer filters.</strong> The <em>Live only</em> switch is on by default, so
                  the drawer shows what customers can see; switch it off to review dishes that are
                  hidden (new and imported dishes start hidden). <em>No photo</em> narrows to dishes still
                  waiting for a picture, and <em>Select visible</em> selects only what the filters show.
                </li>
                <li>
                  <strong>Option groups</strong> add customisations (size, extras). Each group has a
                  minimum (0 = optional, 1 or more = required) and a maximum (1 = the customer picks one,
                  more = they can combine choices such as toppings). Groups are created and edited from
                  the dish form: each group attached to a dish has an <em>Edit</em> link next to it.
                  Chosen options ride on the order line and are re-priced from the catalogue at checkout.
                </li>
                <li>
                  <strong>Outlets share the parent menu</strong> — no independent sections;{" "}
                  <code>outlet.menuItems</code> is an allowlist of product ids (empty = full menu shows).
                </li>
                <li>
                  <strong>Hours gate availability.</strong> Restaurant open hours and outlet-specific hours
                  control when customers can order.
                </li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-tv-2" />
            3. Ordering surfaces
          </Card.Title>
          <p className="text-muted mb-2" style={bodyText}>
            All routes land on the same orders API and fire the same vendor notifications (
            <code>newRestaurantOrder</code> to the vendor socket room, plus WhatsApp / push).
          </p>
          <ul className="mb-0 text-muted" style={listStyle}>
            <li>
              <strong>Expo mobile app (and its iOS twin).</strong> A dedicated per-vendor restaurant cart with
              product options / add-ons and outlet support. Mirror customer-facing changes in both apps.
            </li>
            <li>
              <strong>Web storefront.</strong> Customers browse into the restaurant shop page and check out
              with the same server-computed delivery fee.
            </li>
            <li>
              <strong>In-house / phone-in orders.</strong> The <em>+ New In-House Order</em> panel (top-right
              of every vendor dashboard) lets owners, vendor staff, or Lusuku SUPERVISOR / SUPERUSER (via a
              vendor selector) order for a caller or walk-in. Four steps: customer info (phone required),
              menu/cart, delivery address (Google Places), review &amp; pay (COD, MTN MoMo, Airtel, card). It
              posts to <code>/weborders/inhouse</code>: the backend upserts a ghost client keyed on the
              customer&rsquo;s phone (<code>isWalkIn: true</code>, reused on repeat calls — no duplicates) and
              tags the order <code>orderSrc: &quot;InHouse&quot;</code> plus <code>agent_id</code> (who placed
              it). Same socket / WhatsApp notifications as an app order; a WhatsApp failure never blocks the
              order.
            </li>
            <li>
              <strong>POS.</strong> The in-store POS supports restaurant fulfilment for on-premise operations.
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-delivery-fast" />
            4. Order lifecycle &amp; delivery
          </Card.Title>
          <p className="text-muted mb-2" style={bodyText}>
            Restaurant orders follow the platform-wide state machine.
          </p>
          <Row className="g-2 mb-3">
            {LIFECYCLE.map(([state, note], i) => (
              <Col md={6} lg={4} key={state}>
                <div style={stepStyle}>
                  <div style={{ fontSize: 10, color: MUTED }}>STEP {i + 1}</div>
                  <div style={{ fontWeight: 700, fontSize: 12.5, color: ACCENT, letterSpacing: "0.4px" }}>
                    {state}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12, marginTop: 3 }}>
                    {note}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <ul className="mb-3 text-muted" style={listStyle}>
            <li>
              <strong>Three fulfilment modes:</strong> vendor&rsquo;s own riders, authorised 3PL partner
              riders, or Lusuku dispatch. Fee splits differ: 3PL orders split 40% Lusuku / 18% partner / 42%
              rider; vendor in-house delivery is 10% Lusuku / 90% rider. Partners with custom rates on their
              profile override the defaults.
            </li>
            <li>
              <strong>Delivery fees are server-authoritative.</strong> Client-supplied fees are ignored at
              order-create; the server computes the quote and persists <code>deliveryFee</code> and{" "}
              <code>distanceKm</code> on the order.
            </li>
          </ul>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a2b4a" }}>
            How the delivery fee builds up
          </div>
          <p className="text-muted mb-2" style={bodyText}>
            Fee = base UGX 2,000 + tiered per-km charge + rider approach surcharge + pickup-complexity add-on,
            rounded <em>up</em> to the nearest 500, with a hard floor of UGX 5,000 and a soft cap of UGX
            30,000. Distance is road distance (Google Maps, with a straight-line &times;1.3 fallback). If
            vendor or customer coordinates are missing, the customer is charged the UGX 5,000 floor.
          </p>
          <Table responsive hover size="sm" className="mb-2" style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <th>Distance band</th>
                <th>Rate (UGX/km)</th>
              </tr>
            </thead>
            <tbody>
              {FEE_TIERS.map(([band, rate]) => (
                <tr key={band}>
                  <td>{band}</td>
                  <td>{rate}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <ul className="mb-0 text-muted" style={listStyle}>
            <li>
              Rates are cumulative: a 10 km delivery is 3&times;1,000 + 5&times;700 + 2&times;550 = 7,600,
              rounded up to <strong>UGX 8,000</strong>. Typical examples: 2 km &rarr; 5,000 (floor); 6 km
              &rarr; 6,500; 12 km + moderate pickup &rarr; 10,000.
            </li>
            <li>
              <strong>Approach surcharge:</strong> when the rider is more than 1.5 km from the vendor at
              assignment, extra km are billed at UGX 300/km (capped at UGX 1,500); the customer pays 40% of it
              and Lusuku absorbs the rest.
            </li>
            <li>
              <strong>Pickup complexity:</strong> per-vendor add-on of 0 (standard), 500 (moderate — tight
              parking, multi-floor), 1,000 (difficult — gated compound) or 1,500 (premium — hospital/campus).
              Set per vendor from the delivery-fee settings page.
            </li>
            <li>
              All pricing values (base, tiers, surcharge, splits) are configurable at{" "}
              <code>/admin/delivery-fee-settings</code> and go live immediately; every connected admin session
              syncs in real time.
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-money-coins" />
            5. COD &amp; the rider cash threshold
          </Card.Title>
          <p className="text-muted mb-2" style={bodyText}>
            On a Cash-on-Delivery order the rider collects the full order value in cash. Every COD collection
            is tracked on the rider&rsquo;s <strong>held balance</strong> and a threshold cycle is enforced so
            riders never accumulate large amounts of company money.
          </p>
          <ul className="mb-0 text-muted" style={listStyle}>
            <li>
              <strong>Flag.</strong> When the held balance reaches the rider&rsquo;s threshold (configurable
              per rider, platform default otherwise), the rider is flagged and{" "}
              <strong>blocked from new order assignments</strong>. Their app shows a banner with the held
              amount; admins see flagged riders in real time.
            </li>
            <li>
              <strong>Deposit.</strong> The rider taps &ldquo;Deposit to Lusuku&rdquo; in their app — an MTN
              MoMo <strong>PIN prompt</strong> is pulled to their phone (the number must be MTN; non-MTN
              numbers are rejected up front with a clear error). Approving it transfers the held cash to
              Lusuku&rsquo;s collection account; both legs are currently fee-free on MTN.
            </li>
            <li>
              <strong>Unflag.</strong> On confirmation the rider is automatically unflagged, the held balance
              resets to 0, and they resume receiving orders. A declined prompt leaves them flagged (retry
              available); if the confirmation callback is missed, a background job re-checks pending deposits
              every 5 minutes, so an approved deposit always unflags within minutes.
            </li>
            <li>
              <strong>Reconcile.</strong> Confirmed deposits land centrally; the Lusuku internal team
              allocates each deposit to the relevant vendor wallets from the COD reconciliation view. A rider
              who cannot deposit by MTN can hand the cash to Lusuku staff, who press <strong>Receive
              cash</strong> on the Riders page and record it as paid into the Lusuku MTN account (with the
              MTN transaction id) or as cash at hand; the rider is cleared at once. On a delivered
              cash-on-delivery order, <strong>Mark as Paid</strong> opens the same receipt. Admins can
              also adjust a rider&rsquo;s threshold.
            </li>
            <li>
              <strong>Payout offset.</strong> Weekly rider payouts deduct held COD cash: payout = max(0, net
              earnings &minus; COD held + COD already deposited), so a rider is never double-paid for cash
              they collected.
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bank" />
            6. How restaurant money moves
          </Card.Title>
          <ul className="mb-0 text-muted" style={listStyle}>
            <li>
              <strong>Charge.</strong> MTN Mobile Money, Airtel Money, card (Flutterwave) or Cash on Delivery.
            </li>
            <li>
              <strong>Escrow.</strong> Funds settle through the Wallet Service escrow — vendor balance shows{" "}
              <em>pending</em> until delivery is confirmed.
            </li>
            <li>
              <strong>Commission.</strong> Deducted at <code>SETTLED</code>; the released balance increments
              and payouts run via MTN MoMo disbursement with an idempotency key.
            </li>
            <li>
              <strong>COD reconciliation.</strong> Rider COD deposits are received centrally and allocated to
              the relevant vendor wallets by the Lusuku internal team (see section 5).
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bullet-list-67" />
            7. Dashboard surface area
          </Card.Title>
          <p className="text-muted mb-2" style={bodyText}>
            The main restaurant-related surfaces a supervisor can touch.
          </p>
          <Table responsive hover size="sm" className="mb-0" style={tableStyle}>
            <thead style={theadStyle}>
              <tr>
                <th>Surface</th>
                <th>Path</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {SURFACES.map(([surface, path, note]) => (
                <tr key={surface}>
                  <td>{surface}</td>
                  <td>{path}</td>
                  <td className="text-muted">{note}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bulb-63" />
            8. Quick answers
          </Card.Title>
          <QA
            q="A restaurant says it&rsquo;s not getting orders — what do I check first?"
            a="Verification first: sellerVerification must be VERIFIED and each product individually VERIFIED. Then open hours (restaurant and outlet) — closed hours hide the vendor from customers. Finally confirm the vendor dashboard is online, since order alerts arrive over the live socket connection."
          />
          <QA
            q="A customer disputes the delivery fee — how do I explain it?"
            a="It is distance-based: UGX 2,000 base plus a per-km rate that steps down with distance, rounded up to the nearest 500, minimum UGX 5,000, soft-capped at UGX 30,000. A far-away rider or a hard pickup location adds a small surcharge. The exact quote can be reproduced in the fee simulator on the delivery-fee settings page."
          />
          <QA
            q="Why is a rider not receiving new assignments?"
            a="Most often they hit the COD cash threshold and are flagged. They must approve the MTN PIN prompt from the deposit banner in their app; on confirmation they are auto-unflagged (a missed confirmation is recovered by a background re-check within about 5 minutes). If the prompt never arrived, check the number is MTN and have them retry. If they still cannot deposit, take the cash and use Receive cash on the Riders page."
          />
          <QA
            q="How do I place an order for a customer who phoned in?"
            a="Use the + New In-House Order button on the vendor dashboard (supervisors pick the vendor first). Only the phone number is mandatory — the system creates or reuses a walk-in customer record keyed on that phone, and the order is tagged InHouse with your user as the agent."
          />
          <QA
            q="Why doesn&rsquo;t a phone-in order show in the customer&rsquo;s app?"
            a="Orders placed without the customer&rsquo;s real account id attach to a walk-in placeholder record, not their app account, so they won&rsquo;t appear in My Orders. Only orders placed from the customer&rsquo;s own logged-in app link to their account."
          />
          <QA
            q="When does the vendor actually get paid?"
            a="Money sits in escrow as pending until delivery is confirmed. At SETTLED the commission is deducted, the released balance increments, and payouts go out via MTN MoMo disbursement. COD cash arrives via rider deposits and is allocated to vendor wallets by the internal team."
          />
          {IS_DEV && (
            <div className="mt-2">
              <Badge bg="secondary">DEV</Badge>{" "}
              <span className="text-muted" style={bodyText}>
                Locally, new orders only reach a vendor dashboard once it has joined{" "}
                <code>restaurantVendor:&lt;vendorId&gt;</code>; pickup coordinates resolve outlet &rarr;
                parent restaurant &rarr; live lookup.
              </span>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default RestaurantPlatformWiki;
